import { useEffect, useMemo, useState } from 'react';
import { getDeposits } from '../../../../services/depositsService';
import { hydrateFilters } from '../../../../services/productService';
import { getSearchParams, handleSearchParams } from '../../../../helpers/handleSearchParams';
import { BASE_FILTERS, PERIOD_PRESETS, toApiDate } from './constants';

const findPresetById = (id) => PERIOD_PRESETS.find((p) => p.id === id) || PERIOD_PRESETS[0];

/**
 * Hook con todo el estado de filtros del informe de Reposición:
 *   - `presetId` + `dateFrom`/`dateToExclusive` (rango personalizado).
 *   - Multi-select de depósitos.
 *   - Búsqueda libre por familia con debounce.
 *   - Dims de producto (gender / category / subcategory) hidratadas
 *     desde el back.
 *
 * Persiste el estado relevante en la URL para deep-linking.
 */
export const useReplenishmentFilters = () => {
  // Preset inicial — preferir URL, fallback a "Campaña actual".
  const initialPresetId = useMemo(() => {
    const urlPreset = getSearchParams('preset');
    if (urlPreset && PERIOD_PRESETS.some((p) => p.id === urlPreset)) return urlPreset;
    return 'campana-actual';
  }, []);

  const [presetId, setPresetIdInternal] = useState(initialPresetId);

  // Rango efectivo (calculado desde preset o sobreescrito en custom).
  // El preset "campana-actual" inicial se materializa una sola vez al
  // montar; los demás se reevalúan al cambiar `presetId`.
  const [range, setRange] = useState(() => {
    const urlFrom = getSearchParams('dateFrom');
    const urlTo = getSearchParams('dateToExclusive');
    if (urlFrom && urlTo) {
      return { from: new Date(urlFrom), toExclusive: new Date(urlTo) };
    }
    return findPresetById(initialPresetId).range();
  });

  const setPresetId = (id) => {
    setPresetIdInternal(id);
    if (id !== 'custom') {
      const p = findPresetById(id);
      setRange(p.range());
    }
  };

  // En modo custom, el caller puede sobreescribir el rango libremente.
  const setCustomRange = ({ from, toExclusive }) => {
    setPresetIdInternal('custom');
    setRange({ from, toExclusive });
  };

  // Alcance de depósitos en tres dimensiones acumulativas, cada una con su
  // query param homónimo (deposits / depositRoles / depositGroups). Vacío
  // = default del back: perímetro de venta.
  const [depositsValue, setDepositsValue] = useState(() => getSearchParams('deposits') || '');
  const [depositRolesValue, setDepositRolesValue] = useState(() => getSearchParams('depositRoles') || '');
  const [depositGroupsValue, setDepositGroupsValue] = useState(() => getSearchParams('depositGroups') || '');
  const [depositOptions, setDepositOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);

  // Dims de producto.
  const [filters, setFilters] = useState(BASE_FILTERS);

  // Búsqueda por familia: debounce de 300ms.
  const [familySearch, setFamilySearch] = useState(() => getSearchParams('family') || '');
  const [familyQuery, setFamilyQuery] = useState(familySearch);
  useEffect(() => {
    const t = setTimeout(() => setFamilyQuery(familySearch.trim()), 300);
    return () => clearTimeout(t);
  }, [familySearch]);

  const [ready, setReady] = useState(false);
  useEffect(() => {
    Promise.all([
      hydrateFilters(
        Object.keys(BASE_FILTERS)
          .map((k) => BASE_FILTERS[k])
          .reduce((acc, f) => ({ ...acc, [f.slug]: f }), {}),
      ),
      getDeposits({ limit: 1000, excludeGroups: true }),
      getDeposits({ limit: 100, onlyGroups: true }),
    ]).then(([f, d, g]) => {
      setFilters((prev) => ({ ...prev, ...f }));
      setDepositOptions(d.data.data.map((dep) => ({ label: dep.name, value: dep.id })));
      setGroupOptions((g.data.data || []).map((grp) => ({ label: grp.name, value: grp.id })));
      setReady(true);
    });
  }, []);

  // Setter único para el selector de alcance (ver DepositScopeFilter).
  const setDepositScope = ({ all, roles, groups, deposits }) => {
    setDepositsValue(all ? 'ALL' : deposits.join(':'));
    setDepositRolesValue(all ? '' : roles.join(':'));
    setDepositGroupsValue(all ? '' : groups.join(':'));
  };

  const handleFilter = ({ slug, value }) => {
    setFilters((prev) => ({ ...prev, [slug]: { ...prev[slug], value } }));
  };

  const clearFilters = () => {
    setFilters((prev) => {
      const next = { ...prev };
      Object.keys(BASE_FILTERS).forEach((k) => {
        next[k] = { ...next[k], value: null };
      });
      return next;
    });
    // El alcance de depósitos también es un filtro: vuelve al default.
    setDepositsValue('');
    setDepositRolesValue('');
    setDepositGroupsValue('');
  };

  const persistInUrl = () => {
    handleSearchParams('preset', presetId);
    handleSearchParams('dateFrom', toApiDate(range.from));
    handleSearchParams('dateToExclusive', toApiDate(range.toExclusive));
    handleSearchParams('deposits', depositsValue);
    handleSearchParams('depositRoles', depositRolesValue);
    handleSearchParams('depositGroups', depositGroupsValue);
    handleSearchParams('family', familyQuery);
    Object.keys(filters).forEach((k) => handleSearchParams(k, filters[k]?.value));
  };

  const validFilters = useMemo(
    () =>
      Object.keys(filters)
        .map((k) => (filters[k]?.value ? { slug: k, value: filters[k].value } : null))
        .filter(Boolean)
        .reduce((acc, f) => ({ ...acc, [f.slug]: f.value }), {}),
    [filters],
  );

  return {
    presetId,
    setPresetId,
    range,
    setCustomRange,
    depositsValue,
    setDepositsValue,
    depositRolesValue,
    depositGroupsValue,
    setDepositScope,
    depositOptions,
    groupOptions,
    filters,
    handleFilter,
    clearFilters,
    familySearch,
    setFamilySearch,
    familyQuery,
    ready,
    validFilters,
    persistInUrl,
  };
};
